<script>
    import dressing from "@intellectability/platform-common";

    import {ACTIONS} from "@intellectability/platform-common/constants";

    import {
        AutoUpdateInput,
        Icon,
        toast
    } from "@intellectability/platform-common/sv";
    import {userSecurity} from "@intellectability/platform-common/sv/state";

    import {actionCheck} from "@intellectability/platform-common/utils";

    import {MediaQuery} from "svelte/reactivity";
    // 992px is the Bootstrap xl media breakpoint
    let sm = new MediaQuery("(max-width: 576px)");

    import {
        Col,
        Row
    } from "@sveltestrap/sveltestrap";

    /* @type {string|number} userID */
    let {id: userID, isActiveProfile = false} = $props();

    const {
        USER_DETAILS_UPDATE,
        USER_DETAILS_REQUEST
    } = ACTIONS;

    let hasAccess = $derived(actionCheck(userSecurity.userActions));
    let loggedInUserID = $derived(Number(userSecurity.loggedInUser.id) || "");
    let ownProfile = $derived(loggedInUserID === userID);

    let loading = $state(true);

    /**
     * @typedef {object} Notification
     * @property {number} id
     * @property {string} event_name
     * @property {boolean} mandatory
     * @property {boolean} subscribed
     */

    /**
     * @typedef {object} MessagePreferences
     * @property {boolean} message_board - send messages to the internal Message Center board
     * @property {boolean} email - send messages to the user's email address
     * @property {boolean} text_message - send messages to the user's cell phone number
     */

    /**
     * @type {Notification[]}
     */
    let notifications = $state.raw([]);

    let mandatoryNotifications = $derived(notifications.filter(n => n.mandatory));
    let subscribedNotifications = $derived(notifications.filter(n => !n.mandatory));

    let preferences = $state.raw({});

    /**
     * @type MessagePreferences
     */
    let messagePreferences = $derived.by(function () {
        const m = new Map();
        m.set("Notifications in Message Center",
            preferences.system_msg_mb === "Y"
        );
        // allow external messages unless blocked by business rule
        if (!userSecurity.businessRules.includes("block_external_messaging")) {
            m.set("Notifications by Email", {
                field_name: "system_msg_email",
                enabled: preferences.system_msg_email === "Y"
            });
            m.set("Notifications by Text Message", preferences.system_msg_txt === "Y");
        }
        return m;
    });

    let isExistingUser = $derived(Number.isInteger(parseInt(userID, 10)));
    let hasGatekeeperPermissions = $derived(hasAccess(USER_DETAILS_REQUEST));

    let disabled = $derived.by(function () {

        if (!isActiveProfile) {
            return true;
        }

        if (isExistingUser) {
            if (ownProfile === true) {  // Users can always update their own account notifications
                return false;
            }
            // note: gatekeepers can update notifications todo: when accessing user matching their provider agency
            return hasAccess(USER_DETAILS_UPDATE) === false || hasGatekeeperPermissions === false;
        }

        return true;
    });

    $effect(function load() {

        dressing.get(`/users/notifications/${userID}`).then(({data: res}) => {

            const {success, data} = res;
            if (success === true) {
                const {notifications: n, user_preferences} = data;

                if (Array.isArray(n) && n.length > 0) {

                    notifications = n.map(function mandatory_to_bool(notification) {
                        notification.mandatory = notification.mandatory === "Y";
                        return notification;
                    }).toSorted(function (a, b) {
                        return a.event_name.localeCompare(b.event_name);
                    });
                }

                if (Array.isArray(user_preferences)) {
                    preferences = user_preferences[0]; // the preferences are stored in an object in the zeroth element
                }
            }
            else {
                toast.error("An error occurred while loading user notifications");
            }

            loading = false;
        });
    });

    function toggle_notification(result) {

        const {id: user_id, secondary_id: message_id, value} = result;

        dressing.post(`/users/notifications/update`, {
            user_id,
            message_id,
            subscribed: value === "Y"
        });

    }

</script>

<div class="mb-3">
    <h2>
        <Icon icon="comment-lines" class="fa-flip-horizontal me-1"/>
        Notifications
    </h2>
</div>

<!-- Select Message Preferences -->
<div class="alert alert-primary py-4 mb-4">
    <Row class="g-4">
        {#each messagePreferences as [label, selected]}
            <Col xs="12" sm="6" lg="4" class="d-flex">
                <div class="mx-0 mx-lg-auto">
                    {#if label === "Notifications by Text Message"}
                        <div class="align-items-center d-none d-sm-flex mb-2">
                            <label class="form-label fw-bold mb-0" for="text-notifications">
                                {label}
                            </label>
                            <span id={"phone_number_help"} class="ms-2">
                                    <Icon class="pointer text-warning-500"
                                          icon="question-circle" size="lg"
                                          style="font-weight: 700;"/>
                                    <wa-tooltip for={"phone_number_help"} class="text-white fw-normal">
                                        Must have carrier and cell number filled in under Details tab
                                    </wa-tooltip>
                                </span>
                        </div>
                    {:else}
                        <label class="form-label d-none d-sm-block fw-bold"
                               for={"message-preferences-" + label}>{label}</label>
                    {/if}
                    <AutoUpdateInput
                            autoupdate={true}
                            class="mt-0"
                            id={"message-preferences-" + label}
                            fieldName="system_msg_mb"
                            label={sm.current ? label : ""}
                            size="small"
                            type="switch"
                            defaultValue={selected}
                            disabled={disabled}
                            route={`users/update/${userID}`}
                            entityID={userID}
                            successIcon={preferenceSuccessIcon}
                    />
                </div>
            </Col>
        {/each}
    </Row>
</div>

<div class="mb-5">
    <h3>User Notification Subscriptions</h3>
    <wa-divider></wa-divider>

    {#if mandatoryNotifications.length > 0}

        <h4>Mandatory Notifications</h4>
        <table class="table table-striped">
            <thead>
            <tr>
                <th scope="col">Subscribe</th>
                <th scope="col">Event Name</th>
            </tr>
            </thead>
            <tbody>
            {#each mandatoryNotifications as notification}
                <tr>
                    {@render notificationToggle(notification)}
                </tr>
            {/each}
            </tbody>
        </table>
    {/if}

    {#if subscribedNotifications.length > 0}
        {#if mandatoryNotifications.length > 0}
            <h4>Optional Notifications</h4>
        {/if}
    {/if}
    <fieldset {disabled}>
        <table class="table table-striped">
            <thead>
            <tr>
                <th scope="col">Subscribe</th>
                <th scope="col">Event Name</th>
            </tr>
            </thead>
            <tbody>
            {#each subscribedNotifications as notification}
                <tr>
                    {@render notificationToggle(notification)}
                </tr>
            {/each}
            </tbody>
        </table>
    </fieldset>
</div>

{#snippet preferenceSuccessIcon()}
    <Icon icon="check-circle" variant="regular" class="text-primary-700 me-2"/>
{/snippet}

{#snippet notificationToggle(notification)}
    {@const message_id = notification.id}
    {@const mandatory = notification.mandatory}
    {@const subscribed = notification.subscribed}
    <td class="ps-4">
        {#if mandatory}
            <div id={"notification-" + notification.id}
                 style="width: 45px; text-align: center; font-size: 20px; line-height: 0;">
                <Icon icon="circle-exclamation-check" variant="regular"
                      style="color: var(--hrst-warning-800);"/>
            </div>
            <wa-tooltip for={"notification-" + notification.id} placement="top-start" distance="20"
                        style="--padding: 1.25rem; --max-width: 20rem;">
                <div class="fs-6 text-primary-200">
                    This notification is mandatory and cannot be turned off
                </div>
            </wa-tooltip>
        {:else}
            <span>
                <AutoUpdateInput
                        secondaryID={message_id}
                        entityID={userID}
                        type="switch"
                        size="small" class="m-0"
                        defaultValue={subscribed}
                        onupdate={toggle_notification}
                />
            </span>
        {/if}
    </td>
    <td>{notification.event_name}</td>
{/snippet}

<style>
    /* Remove when Catasta is updated */
    :global(div.autoupdate-input-wrapper) {
        margin: 0 !important;
    }

    tr {
        height: 45px;
        vertical-align: middle;
    }
</style>
